import { useQueryClient } from '@tanstack/react-query';
import { getRelatedUsers, getUserSession, getUserSetting, setUserSetting, login, logout } from '../Services/user';
import { useEffect } from 'react';
import { useBizMutation, useBizQuery } from './useBizQuery';
import { searchUsers } from '../Services/admin/user';

const QUERYKEY_CURRENT_USER_SESSION = 'CURRENT_USER_SESSION';
const QUERYKEY_SEARCH_USERS = 'SEARCH_USERS';
const QUERYKEY_PROFILE_RELATED_USERS = 'PROFILE_RELATED_USERS';
const QUERYKEY_USER_SETTING = 'USER_SETTING';

export const useGetUserSession = () => {
	const { data } = useBizQuery([QUERYKEY_CURRENT_USER_SESSION], getUserSession);
	return data;
};

export const useGetUserSetting = () => {
	const wrapperFn = (params: { key: string }) => {
		return getUserSetting(params.key);
	};

	const { mutate, statusState, data } = useBizMutation(wrapperFn);

	return {
		getSetting: (key: string) => {
			mutate({ key });
		},
		settingValue: data,
		statusState,
	};
};

export const useSetUserSetting = () => {
	const wrapperFn = (params: { key: string; value: string }) => {
		return setUserSetting(params.key, params.value);
	};
	const queryClient = useQueryClient();
	const { mutate, statusState, data } = useBizMutation(wrapperFn);

	useEffect(() => {
		if (statusState === 'SUCCESS' && data) {
			queryClient.invalidateQueries({ queryKey: [QUERYKEY_USER_SETTING, data.Key] });
		}
	}, [statusState, data]);

	return {
		setSetting: (key: string, value: string) => mutate({ key, value }),
		result: data,
		statusState,
	};
};

export const useLogin = () => {
	const loginWrapperFn = (params: { userName: string; password: string }) => {
		return login(params.userName, params.password);
	};
	const queryClient = useQueryClient();
	const { mutate, statusState, data } = useBizMutation(loginWrapperFn);

	useEffect(() => {
		if (statusState === 'SUCCESS' && data?.Token) {
			queryClient.invalidateQueries({ queryKey: [QUERYKEY_CURRENT_USER_SESSION] });
		}
	}, [statusState, data]);

	return {
		login: (userName: string, password: string) => mutate({ userName, password }),
		loginResult: data,
		statusState,
	};
};

export const useLogout = () => {
	const queryClient = useQueryClient();
	const { mutate, statusState, data: redirectUrl } = useBizMutation(logout);

	useEffect(() => {
		if (statusState === 'SUCCESS') {
			queryClient.invalidateQueries({ queryKey: [QUERYKEY_CURRENT_USER_SESSION] });
		}
	}, [statusState]);

	return {
		logout: () => mutate({}),
		redirectUrl,
		statusState,
	};
};

export const useSearchUsers = (
	searchTerm: string,
	pageIndex: number,
	pageLength: number,
	includeInactive: boolean,
	roleId?: number
) => {
	const searchUsersWrapperFn = () => {
		return searchUsers(searchTerm, pageIndex, pageLength, includeInactive, roleId);
	};

	const { data, statusState } = useBizQuery(
		[QUERYKEY_SEARCH_USERS, searchTerm, pageIndex, pageLength, includeInactive, roleId],
		searchUsersWrapperFn
	);
	return { users: data, statusState };
};

export const useGetRelatedUsers = () => {
	const { data } = useBizQuery([QUERYKEY_PROFILE_RELATED_USERS], getRelatedUsers);
	return data ?? undefined;
};
